import * as React from "react";
import * as TagManager from "react-gtm-module";
import { useAnalyticsConfiguration } from "../../providers/analytics-configuration-provider";

interface UseGtmConsumerInterface {
  pushToTheDataLayer: (event: any) => void;
  consumerInitializationGuard: <AnalyticsInterface extends unknown>(props: {
    initializedInterface: AnalyticsInterface;
  }) => AnalyticsInterface;
}

export const useGtm = () => {
  const { isConfigurationLoaded, analyticsConfiguration } =
    useAnalyticsConfiguration();
  const { gtm } = analyticsConfiguration || {};
  const { enabled, gtmId } = gtm || {};

  localStorage.setItem("gtmEnabled", enabled as unknown as string);
  localStorage.setItem("gtmId", gtmId as string);

  const pushToTheDataLayer = (event: any) => {
    let gtmEnabled = localStorage.getItem("gtmEnabled") === "true";
    let id = localStorage.getItem("gtmId") as string;

    if (gtmEnabled && id) {
      TagManager.initialize({
        gtmId: id,
        events: event,
      });
    }
  };

  const consumerInitializationGuard: UseGtmConsumerInterface["consumerInitializationGuard"] =
    React.useCallback(
      <AnalyticsInterface extends unknown>(props: {
        initializedInterface: AnalyticsInterface;
      }): AnalyticsInterface => {
        return React.useMemo(
          () =>
            ({
              ...(isConfigurationLoaded
                ? (props.initializedInterface as { [key: string]: any })
                : {}),
            } as AnalyticsInterface),
          [
            isConfigurationLoaded,
            pushToTheDataLayer,
            props.initializedInterface,
          ]
        );
      },
      [isConfigurationLoaded, pushToTheDataLayer]
    );

  const value: UseGtmConsumerInterface = React.useMemo(
    () => ({
      pushToTheDataLayer,
      consumerInitializationGuard,
    }),
    [pushToTheDataLayer, consumerInitializationGuard]
  );
  return value;
};
